import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { HouseholdName, MemberName } from '@/components/Present';
import { usePresentation } from '@/context/PresentationContext';
import { FACILITY_DETAIL } from '@/constants/testIds';
import { Plus, User, ArrowRight, Warning, UsersThree, EnvelopeSimple, X, DotsThreeVertical, PencilSimple, Trash } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import ConfirmDialog from '@/components/ConfirmDialog';
import DoctorPicker from '@/components/DoctorPicker';
import HouseholdBoardTab from '@/components/HouseholdBoardTab';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

function CoordinatorsDialog({ facilityId }) {
    const qc = useQueryClient();
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState('');

    const { data } = useQuery({
        queryKey: ['coordinators', facilityId],
        queryFn: async () => (await api.get(`/facilities/${facilityId}/coordinators`)).data,
        enabled: open,
    });

    const invite = async () => {
        if (!email.trim()) return;
        try {
            const { data: res } = await api.post(`/facilities/${facilityId}/coordinators`, { email });
            const msg = { added: 'Coordinator added', invited: 'Invitation queued — they will join when they sign in', already_member: 'Already a coordinator', already_invited: 'Already invited' }[res.status] || 'Done';
            toast.success(msg); setEmail('');
            qc.invalidateQueries({ queryKey: ['coordinators', facilityId] });
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    };
    const remove = async (target) => {
        try {
            await api.delete(`/facilities/${facilityId}/coordinators/${encodeURIComponent(target)}`);
            toast.success('Removed');
            qc.invalidateQueries({ queryKey: ['coordinators', facilityId] });
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button data-testid={FACILITY_DETAIL.manageCoordinators} variant="outline" className="rounded-full">
                    <UsersThree size={16} className="mr-2" /> Coordinators
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle className="font-heading text-2xl">Household coordinators</DialogTitle></DialogHeader>
                <p className="text-sm text-slate-600 -mt-1">Anyone here can see and update this household. If they haven&apos;t signed in yet, we&apos;ll queue an invitation.</p>
                <div className="flex items-center gap-2 pt-2">
                    <Input data-testid={FACILITY_DETAIL.coordinatorEmailInput} type="email" placeholder="name@example.com"
                        value={email} onChange={e => setEmail(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') invite(); }} />
                    <Button data-testid={FACILITY_DETAIL.coordinatorInvite} onClick={invite} className="rounded-full bg-primary text-primary-foreground shrink-0">Invite</Button>
                </div>
                <div className="mt-2">
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">Members</div>
                    <div className="rounded-lg border border-slate-200 divide-y divide-slate-200">
                        {(data?.coordinators || []).map(c => (
                            <div key={c.user_id} data-testid={FACILITY_DETAIL.coordinatorRow} className="flex items-center gap-3 px-3 py-2.5">
                                {c.picture ? <img src={c.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                                    : <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-xs font-semibold">{(c.name || c.email || '?').slice(0, 1)}</div>}
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm text-slate-900 truncate">{c.name}{c.user_id === user?.user_id && <span className="text-slate-400 text-xs"> · you</span>}</div>
                                    <div className="text-xs text-slate-500 truncate">{c.email}</div>
                                </div>
                                {c.user_id !== user?.user_id && (
                                    <button data-testid={FACILITY_DETAIL.coordinatorRemove} onClick={() => remove(c.user_id)}
                                        className="text-slate-400 hover:text-red-600 transition-colors duration-100 p-1" aria-label="Remove">
                                        <X size={16} />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                    {(data?.invited_emails || []).length > 0 && (
                        <>
                            <div className="text-[11px] uppercase tracking-widest text-slate-500 mt-4 mb-2">Pending invites</div>
                            <div className="rounded-lg border border-dashed border-slate-300 divide-y divide-slate-200">
                                {(data?.invited_emails || []).map(em => (
                                    <div key={em} data-testid={FACILITY_DETAIL.invitedRow} className="flex items-center gap-3 px-3 py-2.5">
                                        <EnvelopeSimple size={18} className="text-slate-400" />
                                        <div className="flex-1 min-w-0 text-sm text-slate-700 truncate">{em}</div>
                                        <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded bg-amber-50 text-amber-800">Pending</span>
                                        <button onClick={() => remove(em)} className="text-slate-400 hover:text-red-600 p-1" aria-label="Cancel invite">
                                            <X size={16} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function MemberForm({ facilityId, initial, onSaved }) {
    const [f, setF] = useState({
        name: initial?.name || '',
        dob: initial?.dob || '',
        phone: initial?.phone || '',
        email: initial?.email || '',
        allergies: (initial?.allergies || []).join(', '),
        conditions: (initial?.conditions || []).join(', '),
        primary_provider: initial?.primary_provider || '',
        primary_provider_id: initial?.primary_provider_id || '',
    });
    const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
    const save = async () => {
        if (!f.name.trim()) { toast.error('Name required'); return; }
        try {
            const payload = {
                name: f.name,
                dob: f.dob || null,
                phone: f.phone || null,
                email: f.email || null,
                allergies: f.allergies.split(',').map(s => s.trim()).filter(Boolean),
                conditions: f.conditions.split(',').map(s => s.trim()).filter(Boolean),
                primary_provider: f.primary_provider || null,
                primary_provider_id: f.primary_provider_id || '',
            };
            if (initial) {
                await api.patch(`/patients/${initial.patient_id}`, payload);
            } else {
                await api.post('/patients', { facility_id: facilityId, ...payload });
            }
            toast.success('Saved');
            onSaved?.();
        } catch { toast.error('Failed'); }
    };
    return (
        <>
            <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                    <Label>Name</Label>
                    <Input data-testid={FACILITY_DETAIL.patientNameInput} value={f.name} onChange={upd('name')} placeholder='e.g., "Hank Reyes" or "Grandma Anne"' />
                </div>
                <div>
                    <Label>Date of birth</Label>
                    <Input data-testid={FACILITY_DETAIL.patientDobInput} type="date" value={f.dob} onChange={upd('dob')} />
                </div>
                <div>
                    <Label>Phone</Label>
                    <Input data-testid={FACILITY_DETAIL.patientPhoneInput} value={f.phone} onChange={upd('phone')} />
                </div>
                <div className="col-span-2">
                    <Label>Email</Label>
                    <Input data-testid={FACILITY_DETAIL.patientEmailInput} value={f.email} onChange={upd('email')} placeholder="For check-in delivery" />
                </div>
                <div className="col-span-2">
                    <Label>Allergies (comma-separated)</Label>
                    <Input data-testid={FACILITY_DETAIL.patientAllergiesInput} value={f.allergies} onChange={upd('allergies')} placeholder="Penicillin, Sulfa" />
                </div>
                <div className="col-span-2">
                    <Label>Conditions (comma-separated)</Label>
                    <Input data-testid={FACILITY_DETAIL.patientConditionsInput} value={f.conditions} onChange={upd('conditions')} placeholder="Hypertension, BPH" />
                </div>
                <div className="col-span-2">
                    <Label>Primary doctor</Label>
                    <DoctorPicker patientId={initial?.patient_id} testid="member-primary-doctor-picker"
                        value={f.primary_provider ? { provider_id: f.primary_provider_id, name: f.primary_provider } : null}
                        onChange={(d) => setF({ ...f, primary_provider_id: d?.provider_id || '', primary_provider: d?.name || '' })} />
                </div>
            </div>
            <DialogFooter>
                <Button data-testid={FACILITY_DETAIL.patientSubmit} onClick={save} className="rounded-full bg-primary text-primary-foreground">
                    {initial ? 'Save' : 'Add family member'}
                </Button>
            </DialogFooter>
        </>
    );
}

export default function HouseholdDetail() {
    const { facilityId } = useParams();
    const qc = useQueryClient();

    const { data: facility } = useQuery({
        queryKey: ['facility', facilityId],
        queryFn: async () => (await api.get(`/facilities/${facilityId}`)).data,
    });
    const { data: patients = [] } = useQuery({
        queryKey: ['patients', facilityId],
        queryFn: async () => (await api.get(`/patients`, { params: { facility_id: facilityId } })).data,
    });

    const [addOpen, setAddOpen] = useState(false);
    const [editMember, setEditMember] = useState(null);
    const [delMember, setDelMember] = useState(null);
    const [editHH, setEditHH] = useState(false);
    const [delHH, setDelHH] = useState(false);

    const invalidate = () => {
        qc.invalidateQueries({ queryKey: ['patients', facilityId] });
        qc.invalidateQueries({ queryKey: ['facility', facilityId] });
    };

    const doDeleteMember = async () => {
        try {
            await api.delete(`/patients/${delMember.patient_id}`);
            toast.success('Family member removed');
            setDelMember(null); invalidate();
        } catch { toast.error('Failed'); }
    };
    const doDeleteHH = async () => {
        try {
            await api.delete(`/facilities/${facilityId}`);
            toast.success('Household removed');
            window.location.href = '/households';
        } catch { toast.error('Failed'); }
    };
    const saveHH = async (name, kind) => {
        try {
            await api.patch(`/facilities/${facilityId}`, { name, kind });
            toast.success('Saved'); setEditHH(false); invalidate();
        } catch { toast.error('Failed'); }
    };

    return (
        <div data-testid={FACILITY_DETAIL.root} className="px-6 md:px-10 py-8 max-w-7xl">
            <div className="mb-2 text-[11px] uppercase tracking-[0.2em] text-slate-500">
                <Link to="/households" className="hover:underline">Households</Link> · {facility?.kind}
            </div>
            <header className="mb-8 flex items-end justify-between gap-4">
                <div>
                    <h1 className="font-heading text-4xl md:text-5xl text-slate-900">{facility?.name ? <HouseholdName name={facility.name} /> : '…'}</h1>
                    <p className="text-slate-600 mt-2">{patients.length} member{patients.length === 1 ? '' : 's'}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <CoordinatorsDialog facilityId={facilityId} />
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" className="rounded-full px-3"><DotsThreeVertical size={18} weight="bold" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem data-testid={FACILITY_DETAIL.editHousehold} onClick={() => setEditHH(true)}><PencilSimple size={14} className="mr-2" /> Edit household</DropdownMenuItem>
                            <DropdownMenuItem data-testid={FACILITY_DETAIL.deleteHousehold} onClick={() => setDelHH(true)} className="text-red-600 focus:text-red-700"><Trash size={14} className="mr-2" /> Delete household</DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                    <Dialog open={addOpen} onOpenChange={setAddOpen}>
                        <DialogTrigger asChild>
                            <Button data-testid={FACILITY_DETAIL.addPatientButton} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                                <Plus size={16} className="mr-1" /> Add family member
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-lg">
                            <DialogHeader><DialogTitle className="font-heading text-2xl">New family member</DialogTitle></DialogHeader>
                            <MemberForm facilityId={facilityId} onSaved={() => { setAddOpen(false); invalidate(); }} />
                        </DialogContent>
                    </Dialog>
                </div>
            </header>

            {patients.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-300 py-16 text-center">
                    <User size={40} className="mx-auto text-slate-400" />
                    <div className="font-heading text-2xl mt-3 text-slate-800">No family members yet</div>
                    <p className="text-slate-500 text-sm mt-1">Add someone to start keeping their profile up to date.</p>
                </div>
            ) : (
                <Tabs defaultValue="members">
                    <TabsList>
                        <TabsTrigger value="members" data-testid="household-tab-members">Family members</TabsTrigger>
                        <TabsTrigger value="board" data-testid="household-tab-board">Board</TabsTrigger>
                    </TabsList>
                    <TabsContent value="members" className="mt-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {patients.map(p => (
                        <div key={p.patient_id} data-testid={FACILITY_DETAIL.patientCard}
                            className="group rounded-xl border border-slate-200 bg-card p-5 shadow-sm hover:border-primary transition-colors duration-150 relative">
                            <div className="absolute top-3 right-3">
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <button className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500" aria-label="More">
                                            <DotsThreeVertical size={18} weight="bold" />
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem data-testid={FACILITY_DETAIL.memberEdit} onClick={() => setEditMember(p)}><PencilSimple size={14} className="mr-2" /> Edit</DropdownMenuItem>
                                        <DropdownMenuItem data-testid={FACILITY_DETAIL.memberDelete} onClick={() => setDelMember(p)} className="text-red-600 focus:text-red-700"><Trash size={14} className="mr-2" /> Delete</DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                            <Link to={`/members/${p.patient_id}`} className="block">
                                <div className="flex items-start justify-between pr-6">
                                    <div className="min-w-0">
                                        <div className="font-heading text-xl text-slate-900 truncate"><MemberName name={p.name} /></div>
                                        <div className="text-xs text-slate-500 mt-1">{p.dob ? `DOB ${p.dob}` : '—'} · {p.phone || 'no phone'}</div>
                                    </div>
                                    {p.allergies?.length > 0 && (
                                        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded bg-red-50 text-red-700 border border-red-100">
                                            <Warning size={11} weight="fill" /> Allergy
                                        </span>
                                    )}
                                </div>
                                <div className="mt-4 flex flex-wrap gap-1">
                                    {(p.conditions || []).slice(0, 3).map(c => <span key={c} className="text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-600">{c}</span>)}
                                </div>
                                <div className="mt-5 flex items-center justify-between text-sm">
                                    <span className={p.open_items > 0 ? 'text-[#B45309] font-medium' : 'text-slate-500'}>
                                        {p.open_items} open item{p.open_items === 1 ? '' : 's'}
                                    </span>
                                    <span data-testid={FACILITY_DETAIL.openPatient} className="inline-flex items-center gap-1 text-primary group-hover:translate-x-0.5 transition-transform duration-150">
                                        Open profile <ArrowRight size={14} />
                                    </span>
                                </div>
                            </Link>
                        </div>
                    ))}
                        </div>
                    </TabsContent>
                    <TabsContent value="board" className="mt-4">
                        <HouseholdBoardTab facilityId={facilityId} />
                    </TabsContent>
                </Tabs>
            )}

            <Dialog open={!!editMember} onOpenChange={(v) => !v && setEditMember(null)}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle className="font-heading text-2xl">Edit family member</DialogTitle></DialogHeader>
                    {editMember && <MemberForm facilityId={facilityId} initial={editMember} onSaved={() => { setEditMember(null); invalidate(); }} />}
                </DialogContent>
            </Dialog>

            <ConfirmDialog
                open={!!delMember} onOpenChange={(v) => !v && setDelMember(null)}
                title={`Remove ${delMember?.name}?`}
                description={`This removes ${delMember?.name} and all of their records — appointments, medications, lab results, care notes, to-dos, and sent check-ins. This can't be undone.`}
                onConfirm={doDeleteMember}
                confirmLabel="Yes, remove them"
            />

            <Dialog open={editHH} onOpenChange={setEditHH}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading text-2xl">Edit household</DialogTitle></DialogHeader>
                    <EditHHForm facility={facility} onSave={saveHH} onCancel={() => setEditHH(false)} />
                </DialogContent>
            </Dialog>

            <ConfirmDialog
                open={delHH} onOpenChange={setDelHH}
                title="Delete this household?"
                description={facility ? `This removes "${facility.name}" and everyone in it. This can't be undone.` : ''}
                onConfirm={doDeleteHH}
                confirmLabel="Yes, delete household"
            />
        </div>
    );
}

function EditHHForm({ facility, onSave }) {
    const [name, setName] = useState(facility?.name || '');
    const [kind, setKind] = useState(facility?.kind || 'household');
    return (
        <>
            <div className="space-y-3">
                <div>
                    <Label>Name</Label>
                    <Input value={name} onChange={e => setName(e.target.value)} />
                </div>
                <div>
                    <Label>Kind</Label>
                    <Input value={kind} onChange={e => setKind(e.target.value)} placeholder="household, clinic, senior community…" />
                </div>
            </div>
            <DialogFooter>
                <Button onClick={() => onSave(name, kind)} className="rounded-full bg-primary text-primary-foreground">Save</Button>
            </DialogFooter>
        </>
    );
}
